import { useEffect, useState } from "react";
import CircleTorunamentAvatar from "../../../utils/tournament/tournament_profile_img";
import { jwtDecode } from "jwt-decode";
import { useSelector } from "react-redux";
import { showToast } from "../../../components/ToastNotification";
import {
  FaCity,
  FaDollarSign,
  FaTrophy,
  FaCalendarAlt,
  FaBuilding,
  FaPhoneAlt,
  FaCheckCircle,
} from "react-icons/fa";

const DetailsTab = ({ tournament }) => {
  const [image, setImage] = useState(null);
  const token = useSelector((state) => state.user.token);
  const [userId, setUserId] = useState("");
  const [preview, setPreview] = useState(tournament.picture_url || null);
  const [showUpdateButton, setShowUpdateButton] = useState(false);
  const [isOpen, setIsOpen] = useState(true); // estado local solo para switch
  const isTournamentOwner = userId === tournament.user_id;

  useEffect(() => {
    if (token) {
      const decodedToken = jwtDecode(token);
      setUserId(decodedToken.id);
    }
  }, [token]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setShowUpdateButton(true);

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdate = async () => {
    if (!image) return;

    const formData = new FormData();
    formData.append("tournament_image", image);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/tournaments/${tournament.id}/image`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        },
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.message);

      showToast.success("Foto del torneo actualizada con éxito");
      setShowUpdateButton(false);
      setImage(null);
    } catch (error) {
      console.error(error);
      showToast.error("Error al actualizar la foto del torneo");
    }
  };

  return (
    <div className="max-w-3xl mx-auto bg-gray-50 rounded-2xl shadow-lg p-6 space-y-6">
      <div className="flex justify-center">
        {isTournamentOwner ? (
          <CircleTorunamentAvatar
            size={140}
            imageSrc={preview}
            onImageSelect={handleImageChange}
            className="border-4 border-gradient-to-r from-blue-400 via-purple-500 to-pink-500 shadow-xl"
          />
        ) : preview ? (
          <img
            src={preview}
            alt="Tournament"
            className="w-36 h-36 rounded-full object-cover border-4 border-yellow-400 shadow-md"
          />
        ) : (
          <div className="w-36 h-36 rounded-full flex items-center justify-center bg-gray-200 border-4 border-yellow-400 shadow-md">
            <FaTrophy size={70} color="#E5960E" />
          </div>
        )}
      </div>

      {/* Botón actualizar solo dueño */}
      {isTournamentOwner && showUpdateButton && (
        <div className="flex justify-center">
          <button
            onClick={handleUpdate}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-full font-semibold shadow-md hover:scale-105 transform transition"
          >
            Actualizar Imagen
          </button>
        </div>
      )}

      {/* Switch de inscripciones solo dueño */}
      {isTournamentOwner && (
        <div className="flex justify-center items-center space-x-3">
          <span className="font-semibold text-gray-700">
            {isOpen ? "Inscripciones abiertas" : "Inscripciones cerradas"}
          </span>
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className={`relative inline-flex h-6 w-12 items-center rounded-full transition ${
              isOpen ? "bg-green-500" : "bg-gray-400"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                isOpen ? "translate-x-7" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      )}

      {/* Mensaje de inscripciones abiertas para usuarios que no son dueños */}
      {!isTournamentOwner && isOpen && (
        <div className="flex justify-center items-center space-x-2 text-green-600 font-semibold">
          <FaCheckCircle />
          <span>Inscripciones abiertas</span>
        </div>
      )}

      {/* Nombre del torneo */}
      <h2 className="text-2xl font-bold text-gray-800 text-center">
        {tournament.tournament_name}
      </h2>

      {/* Info del torneo */}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex items-center p-3 bg-white rounded-xl shadow hover:shadow-lg transition">
          <FaBuilding className="text-emerald-500 mr-3" />
          <span>
            <strong>Organizador:</strong>{" "}
            {tournament.organization_name || "No especificado"}
          </span>
        </div>

        {/* Teléfono organizador */}
        <div className="flex items-center p-3 bg-white rounded-xl shadow hover:shadow-lg transition">
          <FaPhoneAlt className="text-green-500 mr-3" />
          {tournament.organization_phone ? (
            <a
              href={`tel:${tournament.organization_phone}`}
              className="text-blue-600 hover:underline"
            >
              {tournament.organization_phone}
            </a>
          ) : (
            <span>No especificado</span>
          )}
        </div>
        <div className="flex items-center p-3 bg-white rounded-xl shadow hover:shadow-lg transition">
          <FaTrophy className="text-yellow-500 mr-3" />
          <span>
            <strong>Tipo:</strong> {tournament.tournament_type}
          </span>
        </div>
        <div className="flex items-center p-3 bg-white rounded-xl shadow hover:shadow-lg transition">
          <FaCalendarAlt className="text-blue-400 mr-3" />
          <span>
            <strong>Edición:</strong> {tournament.edition}
          </span>
        </div>
        <div className="flex items-center p-3 bg-white rounded-xl shadow hover:shadow-lg transition">
          <FaCity className="text-green-400 mr-3" />
          <span>
            <strong>Ciudad:</strong> {tournament.city}
          </span>
        </div>
        <div className="flex items-center p-3 bg-white rounded-xl shadow hover:shadow-lg transition">
          <FaDollarSign className="text-purple-500 mr-3" />
          <span>
            <strong>Cuota:</strong> ${tournament.registration_fee}
          </span>
        </div>
      </div>
    </div>
  );
};

export default DetailsTab;
